<template>
  <div>
    <button
      type="button"
      class="brand-button flex transform items-center gap-2 whitespace-nowrap py-3 hover:-translate-y-0.5"
      @click="$modal.show('sections')"
    >
      <span class="text-sm font-normal">Add/Manage New Topic </span>
      <PlusSmall custom-style="h-4 w-4" />
    </button>
    <!-- Modal -->
    <ModalComponent
      name="sections"
      width="800"
      height="auto"
      :adaptive="true"
      :scrollable="true"
      :click-to-close="true"
    >
      <div class="z-[50] flex items-center justify-between p-5">
        <h3 class="flex items-center gap-2 text-lg text-gray-900 lg:text-lg">
          <BookmarkIcon class="h-5 w-5" />
          <span>Manage Topics</span>
        </h3>
        <button
          type="button"
          @click="$modal.hide('sections')"
        >
          <TimesSmall />
        </button>
      </div>
      <!-- Data -->
      <div class="border-gray overflow-hidden border-t px-1 shadow-sm">
        <table
          class="min-w-full table-auto"
          width="100%"
        >
          <thead class="bg-gray-50">
            <tr>
              <th
                scope="col"
                class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700"
              >
                Theme
              </th>
              <th
                scope="col"
                class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700"
              >
                Order
              </th>
              <th
                scope="col"
                class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700"
              >
                Topic Name
              </th>
              <th
                scope="col"
                class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700"
              >
                Description
              </th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(section, index) in sections"
              :key="`section-${index}`"
              class="bg-white text-sm text-gray-500"
            >
              <td class="w-[100px] p-2">
                <select
                  v-if="!section.id"
                  v-model="loadedSections[index].theme"
                  class="w-full px-6 py-2 outline-none ring-2 ring-emerald-200"
                  @change="checkThemeDuplicates"
                >
                  <option value="G">
                    G
                  </option>
                  <option value="E">
                    E
                  </option>
                  <option value="S">
                    S
                  </option>
                </select>
                <span
                  v-else
                  class="px-6 py-3"
                >{{
                  loadedSections[index].theme
                }}</span>
              </td>
              <td class="w-[70px] p-2">
                <input
                  v-if="!section.id"
                  v-model="loadedSections[index].order"
                  type="string"
                  class="w-full px-4 py-3 outline-none ring-2 ring-emerald-200"
                  @input="checkThemeDuplicates"
                >
                <span
                  v-else
                  class="block px-6 py-3"
                >{{
                  loadedSections[index].order
                }}</span>
              </td>
              <td class="b-2 whitespace-nowrap">
                <input
                  v-if="!section.id"
                  v-model="loadedSections[index].title"
                  type="string"
                  class="px-4 py-3 outline-none ring-2 ring-emerald-200"
                >
                <span
                  v-else
                  class="px-6 py-3"
                >{{
                  loadedSections[index].title
                }}</span>
              </td>
              <td class="pb-2">
                <input
                  v-if="dataEditable || !section.id"
                  v-model="loadedSections[index].description"
                  type="string"
                  class="px-6 py-3 outline-none ring-2 ring-emerald-200"
                >
                <p
                  v-else
                  class="px-6 py-3"
                  :title="loadedSections[index].description"
                >
                  {{ loadedSections[index].description }}
                </p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="flex items-center justify-between p-5">
        <GhostButton
          type="button"
          @click="addSection"
        >
          Add Topic
        </GhostButton>
        <div class="flex items-center justify-end">
          <PrimaryButton
            v-if="canClickToEdit"
            type="button"
            @click="dataEditable = true"
          >
            Edit
          </PrimaryButton>
          <PrimaryButton
            v-else
            type="button"
            class="brand-button"
            @click="saveSections"
          >
            Save
          </PrimaryButton>
        </div>
      </div>
    </ModalComponent>
  </div>
</template>

<script>
export default {
  name: 'AddManageSectionsButton',

  props: {
    sections: {
      type: Array,
      required: true,
    },
  },

  emits: ['sectionsUpdated'],

  data() {
    return {
      loadedSections: this.sections,
      dataEditable: false,
      uneditedData: [],
    }
  },

  computed: {
    canClickToEdit() {
      return (
        !this.dataEditable
        && !this.loadedSections.some(section => !section.id)
      )
    },
  },

  watch: {
    sections(newSections) {
      this.loadedSections = newSections
      this.uneditedData = newSections.map(({ description }) => description)
    },
  },

  methods: {
    addSection() {
      this.loadedSections.push({
        theme: 'G',
        order: '',
        title: '',
        description: '',
      })
    },
    async saveSections() {
      const sections = this.loadedSections
      if (
        sections.some(({ order, title, description }) =>
          [order, title, description].includes(''),
        )
      ) {
        this.$toast.error('Please fill in all fields!', {
          timeout: 5000,
          position: 'top-right',
        })
        return
      }

      if (sections.some(section => !section.id)) {
        const sectionsData = [...sections]
          .filter(section => !section.id)
          .map((section) => {
            return {
              theme: section.theme,
              order: section.order,
              title: section.title,
              description: section.description,
            }
          })
        await this.$apis.allTenants
          .createSections(sectionsData)
          .then(() => this.handleApiSuccess())
      }

      const editedData = [...sections]
        .filter(section => section.id)
        .map(({ description }) => description)
      if (
        JSON.stringify(this.uneditedData) !== JSON.stringify(editedData)
        && this.uneditedData.length === editedData.length
      ) {
        const editedIndexes = []
        editedData.forEach((data, index) => {
          if (this.uneditedData[index] !== data) {
            editedIndexes.push(index)
          }
        })
        const editedSections = [...sections].filter(
          (section, index) => section.id && editedIndexes.includes(index),
        )
        await Promise.all(
          editedSections.map(({ sect_id, description }) =>
            this.$apis.allTenants.updateSection(sect_id, { description }),
          ),
        ).then(() => this.handleApiSuccess())
      }

      this.dataEditable = false
      this.$modal.hide('sections')
      this.$emit('sectionsUpdated')
    },
    handleApiSuccess() {
      this.$toast.success('All the Tenants have been updated!', {
        timeout: 3000,
        position: 'top-right',
      })
    },
    checkThemeDuplicates() {
      const themeOrders = [...this.loadedSections].map(({ theme, order }) => {
        return `${theme}${order}`
      })
      const uniqueThemeOrders = [...new Set(themeOrders)]
      if (themeOrders.length > uniqueThemeOrders.length) {
        this.$toast.error('Duplicated Order within the same theme!', {
          timeout: 5000,
          position: 'top-right',
        })
      }
    },
  },
}
</script>
